import { isNil, reject } from 'ramda';
import { getVenueMatchesRange, mapSlugToFriendlyUrlSlug } from '@bridebook/toolbox/src';
import { getPageSize } from '@bridebook/toolbox/src/search-suppliers/get-page-size';
import { SearchType } from '@bridebook/toolbox/src/search-suppliers/types';
import type { IFetchSearchPromiseArgs } from '@bridebook/web/lib/search/types';
import getTargeting from './get-targeting';
import { getSearchRequestArea } from './get-search-request-area';
import type { TMakeSearchRequest } from '@bridebook/toolbox/src/search-suppliers';

/**
 * createSearchQuery - prepare search request query
 * @param slug (required)
 * @param area
 * @param oldSlug
 * @param sort
 * @param guests - used for budget match / capacityDining
 * @param profile
 * @param isLoggedIn
 * @param locationData - static Firebase location props
 * @param countryCode - provided in case of logged-out user and no locationData https://bridebook.atlassian.net/browse/LIVE-14278
 * @param searchOnMove - if true ignore predefined region "bounds"
 * @param size
 * @param isConcierge
 * @param locale
 * @param excludeByWeddingId
 * @param stylesAsSorting
 * @param weddingId
 * @param query
 */
const createSearchQuery = ({
  slug,
  area,
  oldSlug = false,
  sort,
  guests,
  profile = null,
  isLoggedIn,
  locationData = null,
  countryCode,
  searchOnMove,
  size,
  isConcierge,
  locale,
  excludeByWeddingId,
  stylesAsSorting,
  weddingId,
  ...query
}: IFetchSearchPromiseArgs) => {
  const loggedIn = typeof isLoggedIn !== 'undefined' ? isLoggedIn : !!profile?.id;

  // Bounds should be used for all suppliers and venues
  const bounds = searchOnMove
    ? {}
    : loggedIn
      ? locationData?.bounds?.loggedIn
      : locationData?.bounds?.loggedOut || {};

  const searchInCounties = locationData?.searchInCounties || [];

  const finalCountryCode = profile?.l10n?.country || locationData?.countryCode || countryCode;

  const searchType: SearchType | string = searchOnMove
    ? SearchType.Map
    : locationData?.type || SearchType.Town;

  let q: Omit<IFetchSearchPromiseArgs, 'slug'> = {
    type: oldSlug ? slug : mapSlugToFriendlyUrlSlug(slug as NonNullable<typeof slug>).oldSlug,
    area: getSearchRequestArea(area, finalCountryCode),
    ...bounds,
    ...query,
    searchType,
    country:
      locationData?.country ||
      profile?.location?.adminArea?.[1] ||
      profile?.location?.country ||
      '',
    county: locationData?.county || null,
    countryCode: finalCountryCode,
    sort,
    isLoggedIn: loggedIn,
    // isMockLoggedIn: searchType === 'county', // For county type searches always mock logged in request LIVE-15242
    locationInfo: Boolean(Object.keys(locationData || {}).length),
    searchInCounties,
    region: locationData?.region || null,
    size: size ?? getPageSize({ isLoggedIn: loggedIn, countryCode: finalCountryCode }),
    isConcierge,
    locale,
    excludeByWeddingId,
    stylesAsSorting,
    placeId: locationData?.placeId || null,
    weddingId,
  };

  if (guests) query.capacityDining = getVenueMatchesRange(guests);
  if (profile?.id) q = getTargeting({ q, profile });
  return reject(isNil, q) as TMakeSearchRequest;
};

export default createSearchQuery;
